// frontend/app/components/lessonPlans/present/useReadAloud.js
//
// Say the answer out loud, in a room where nobody can lean in and read it.
//
// A projected answer is read by the front row and squinted at by the back.
// Reading it out is what a teacher does next anyway; this saves them doing it
// cold off a wall while thirty people wait.
//
// WHY THE BROWSER'S VOICE AND NOT THE APP'S TTS. The app already has
// text-to-speech — `generate_tts_audio` — and it is the wrong tool for this
// button. It needs a network round trip and a generation pass before the first
// sound, and this button gets pressed in front of a class: the gap between the
// press and the sound is the whole cost. `window.speechSynthesis` starts
// speaking on the same tick, works with the school's wifi down, costs nothing
// per press, and stops dead the instant it is told to. The voice is worse. In
// this room, on this button, that is the right trade.
//
// WHY IT CHUNKS. Two reasons, both about not stopping half way through a
// sentence in front of a class. Chrome silently kills a single utterance that
// runs much past fifteen seconds — a well-known engine bug, and it lands
// mid-word — so the answer goes in as a queue of sentence-sized utterances
// instead of one long one. It also makes "is it still going?" answerable: the
// last chunk's `onend` is the end of the answer, where an engine-truncated
// single utterance would report a finish that never happened.
//
// IT READS TEXT AND NOTHING ELSE. No fetch, no endpoint, no recording. Present
// mode's rule is that nothing here is attributable to a student because no
// student has a device, and speaking a string that is already on the wall
// cannot touch a lesson response, an evaluation or a mastery write.

import { useCallback, useEffect, useRef, useState } from "react";

/**
 * How much text goes into one utterance.
 *
 * Comfortably under Chrome's cutoff at a normal speaking rate, and long enough
 * that chunk boundaries land on sentences the listener was going to pause at
 * anyway rather than chopping the prosody into fragments.
 */
export const SPEECH_CHUNK_MAX_CHARS = 180;

/**
 * A shade under conversational speed. The room is listening to a synthetic
 * voice, at the back, once — a fraction more space between words is worth more
 * here than getting through it quickly.
 */
/**
 * A shade under neutral. Synthesised speech runs level where a person would
 * vary, and the flatness reads as faster than it is; slowing it slightly gives
 * a room time to follow a sentence it is hearing for the first time.
 */
const SPEECH_PITCH = 1.0;

const SPEECH_RATE = 0.95;

// Chrome/Edge can clip the first syllable when `cancel()` and `speak()` happen
// back-to-back. Give the engine a tiny reset window. On a cold page load,
// `getVoices()` is also briefly empty; wait a little longer for voices so the
// first instruction does not fall back to the machine's old default voice.
const SPEECH_START_DELAY_MS = 300;
const SPEECH_VOICE_WAIT_MAX_MS = 700;
const SPEECH_VOICE_POLL_MS = 50;

const SENTENCE_ENDS = [".", "!", "?", "…"];

/**
 * The app's language names, as BCP-47 tags a speech engine understands.
 *
 * WHY THIS TABLE EXISTS. `localLanguage` is not a language code by the time it
 * reaches a component. AITutorApp runs the UI code through `getLocalLanguage`,
 * which turns "en" into "Eng_USA" and "pt" into "Portuguese" for Unity's
 * benefit, and that is the value handed down the lesson tree. Setting
 * `utterance.lang = "Eng_USA"` is not merely untidy: an engine may answer an
 * unusable tag with a `language-unavailable` error, which this hook would then
 * treat as a failed read — a button that goes quiet instead of speaking.
 *
 * Both spellings are accepted because both are real: present mode's own
 * defaults are plain codes and the app's prop is a display name.
 *
 * A region is only ever kept where the app itself supplied one. Everything
 * else stays a bare subtag and lets the engine choose the accent it has,
 * rather than this file inventing one for a language it knows nothing about.
 */
/**
 * How much better one voice is than another, for reading a paragraph aloud to
 * a room. Higher wins.
 *
 * The engine's default is usually the oldest voice on the machine — the flat,
 * clipped one everybody recognises as "computer". The good ones are installed
 * right beside it and are never picked without being asked for:
 *
 *   Natural / Neural   Microsoft's neural voices (Aria, Jenny, Guy, Ryan).
 *                      A different generation of synthesis; the closest thing
 *                      here to a person reading.
 *   Premium / Enhanced Apple's downloaded voices. Markedly better than the
 *                      compact voice of the same name, and confusingly they
 *                      share it, so the label is the only way to tell.
 *   Google             Chrome's network voices. Better than the local
 *                      fallbacks, at the cost of needing the network.
 *   remote             Any non-local voice tends to be a server-side model,
 *                      which tends to be better than anything shipped on disk.
 *
 * Named voices are last and deliberately few: a name is a weak signal that
 * ages badly, and the labels above already catch the ones worth having.
 */
const VOICE_QUALITY = [
  [/natural|neural/i, 100],
  [/premium|enhanced/i, 80],
  [/\bgoogle\b/i, 60],
  [/\b(aria|jenny|guy|ryan|samantha|ava|zoe|serena)\b/i, 40],
];

const scoreVoice = (voice) => {
  const name = String(voice?.name || "");
  let score = VOICE_QUALITY.reduce(
    (total, [pattern, points]) => (pattern.test(name) ? total + points : total),
    0
  );
  // For interactive child guidance, local/native voices are more reliable
  // across browsers (especially Chrome on macOS) than network-backed voices.
  // Keep quality as the main signal, but break close calls in favor of local.
  if (voice && voice.localService === true) score += 25;
  return score;
};

/**
 * The best voice this browser has for `lang`, or null to let the engine decide.
 *
 * Matching is by language first and quality second — a superb French voice
 * reading English is worse than a plain English one, so the language filter is
 * absolute and quality only orders what is left. An empty tag means the app
 * never chose a language, and there the best voice of any language is still the
 * wrong thing to force, so the engine keeps its default.
 */
export const pickVoice = (voices, lang) => {
  const list = Array.isArray(voices) ? voices.filter(Boolean) : [];
  const tag = String(lang || "").trim().toLowerCase();
  if (!list.length || !tag) return null;

  const base = tag.split("-")[0];
  const exact = list.filter((v) => String(v.lang || "").toLowerCase().replace("_", "-") === tag);
  const sameLanguage = list.filter((v) =>
    String(v.lang || "").toLowerCase().startsWith(base)
  );

  // Prefer the exact region the app asked for, then any voice of that language.
  const candidates = exact.length ? exact : sameLanguage;
  if (!candidates.length) return null;

  return candidates.reduce((best, voice) =>
    scoreVoice(voice) > scoreVoice(best) ? voice : best
  );
};

const SPEECH_LANGUAGES = {
  // "auto" means the app never decided, which is exactly what an empty tag
  // tells the engine — use the voice this browser would have used anyway.
  auto: "",
  en: "en-US",
  eng_usa: "en-US",
  "en-in": "en-IN",
  es: "es",
  spanish: "es",
  hi: "hi",
  hindi: "hi",
  fr: "fr",
  french: "fr",
  zh: "zh-CN",
  chinese_simplified: "zh-CN",
  ja: "ja",
  japanese: "ja",
  ko: "ko",
  korean: "ko",
  de: "de",
  german: "de",
  it: "it",
  italian: "it",
  ru: "ru",
  russian: "ru",
  ar: "ar",
  arabic: "ar",
  gu: "gu",
  gujarati: "gu",
  te: "te",
  telugu: "te",
  mr: "mr",
  marathi: "mr",
  pt: "pt",
  portuguese: "pt",
};

/**
 * A tag for the utterance, or "" where the language is not one we can name.
 *
 * Empty is a deliberate answer rather than a failure: it is the spec's "use
 * the default voice", so an unrecognised language still gets read out in
 * whatever voice the browser has. Silence would be the worse outcome.
 *
 * Exported for its tests — a wrong tag here is inaudible until a classroom.
 */
export const speechLanguage = (value) => {
  const raw = String(value || "").trim();
  const key = raw.toLowerCase();
  if (!key) return "";
  if (key in SPEECH_LANGUAGES) return SPEECH_LANGUAGES[key];
  // A tag that is already well formed — "pt-BR" from some other caller — is
  // better passed through than thrown away.
  if (/^[a-z]{2,3}(-[a-z0-9]{2,8})*$/.test(key)) return raw;
  return "";
};

/**
 * The speech engine, or null where there is not one.
 *
 * `typeof window` rather than `window?.` because a runtime without the global
 * throws a ReferenceError on the identifier itself, which optional chaining
 * does not catch.
 */
const speechEngine = () => {
  try {
    if (typeof window === "undefined") return null;
    return window.speechSynthesis || null;
  } catch {
    return null;
  }
};

/**
 * Can this runtime speak at all?
 *
 * The same shape as `isVoiceAnswerSupported`, and for the same reason: a native
 * build and a locked-down browser have no speech engine, and the caller must
 * render nothing rather than a button that does nothing when pressed. A dead
 * control on a projector costs a teacher their place in the lesson.
 */

/**
 * Best-effort browser speech priming from a real user gesture.
 *
 * Chrome/Safari policies and voice loading are most reliable when speech is
 * touched during the click/tap that enables guided mode. We queue a silent,
 * extremely short utterance so the later automatic welcome is not the first
 * speech request the browser sees.
 */
let readAloudPrimed = false;

export const isReadAloudPrimed = () => readAloudPrimed;

export const primeReadAloud = (language = "en") => {
  try {
    if (!isReadAloudSupported()) return false;
    const engine = speechEngine();
    if (!engine) return false;

    engine.resume?.();
    engine.getVoices?.();

    const utterance = new window.SpeechSynthesisUtterance(".");
    const tag = speechLanguage(language);
    if (tag) utterance.lang = tag;
    utterance.volume = 0;
    utterance.rate = 10;
    engine.speak(utterance);
    readAloudPrimed = true;
    return true;
  } catch {
    return false;
  }
};

export const isReadAloudSupported = () => {
  try {
    const engine = speechEngine();
    return (
      !!engine &&
      typeof engine.speak === "function" &&
      typeof window.SpeechSynthesisUtterance === "function"
    );
  } catch {
    return false;
  }
};

/** Split on sentence ends, keeping the terminator with the sentence. */
const splitSentences = (text) => {
  const parts = [];
  let start = 0;

  for (let index = 0; index < text.length; index += 1) {
    const char = text[index];
    const next = text[index + 1];
    // A terminator only ends a sentence when whitespace or the end of the
    // answer follows it, so "37.5 degrees" is not read as two sentences.
    const ends =
      char === "\n" ||
      (SENTENCE_ENDS.includes(char) && (next === undefined || /\s/.test(next)));
    if (!ends) continue;

    const piece = text.slice(start, index + 1).trim();
    if (piece) parts.push(piece);
    start = index + 1;
  }

  const tail = text.slice(start).trim();
  if (tail) parts.push(tail);
  return parts;
};

/** Last resort for a sentence longer than one utterance: break between words. */
const wrapWords = (sentence, maxChars) => {
  const lines = [];
  let line = "";

  sentence.split(/\s+/).forEach((word) => {
    if (!line) line = word;
    else if (line.length + 1 + word.length <= maxChars) line += ` ${word}`;
    else {
      lines.push(line);
      line = word;
    }
  });

  if (line) lines.push(line);
  return lines;
};

/**
 * An answer as the queue of utterances that will read it.
 *
 * Sentences are packed up to the cap rather than sent one at a time: an
 * utterance boundary is a small pause, and a boundary after every short
 * sentence makes the voice sound like it is reading a list.
 *
 * Exported so the splitting can be tested without a speech engine.
 */
export const chunkForSpeech = (value, maxChars = SPEECH_CHUNK_MAX_CHARS) => {
  const text = String(value || "").trim();
  if (!text) return [];

  const chunks = [];
  let current = "";

  const flush = () => {
    if (current) chunks.push(current);
    current = "";
  };

  splitSentences(text).forEach((sentence) => {
    if (sentence.length > maxChars) {
      flush();
      wrapWords(sentence, maxChars).forEach((line) => chunks.push(line));
      return;
    }
    if (!current) current = sentence;
    else if (current.length + 1 + sentence.length <= maxChars) {
      current += ` ${sentence}`;
    } else {
      flush();
      current = sentence;
    }
  });

  flush();
  return chunks;
};

/**
 * @param {object} options
 * @param {string} [options.language] the lesson's language, as either a code
 *   ("pt") or the app's display name ("Portuguese") — see `speechLanguage`.
 *   The engine picks the voice; where it has none for that language it falls
 *   back to its default rather than refusing, which is the right failure for a
 *   classroom.
 *
 * @returns {{
 *   supported: boolean,
 *   speaking: boolean,
 *   paused: boolean,
 *   speak: (text: string, options?: {onEnd?: Function, onError?: Function, immediate?: boolean}) => void,
 *   stop: () => void,
 *   toggle: (text: string) => void,
 *   pause: () => void,
 *   resume: () => void,
 *   togglePause: () => void,
 * }}
 */
export default function useReadAloud({ language = "en" } = {}) {
  const [supported] = useState(isReadAloudSupported);
  const [speaking, setSpeaking] = useState(false);
  const [paused, setPaused] = useState(false);

  // Every read takes the next run number. Engines disagree about what a
  // cancelled utterance fires — Chrome sends `end`, others send `error` — and
  // some send it late. A callback whose run has been superseded does nothing,
  // so a dead utterance can never clear the state of the read that replaced it.
  const runRef = useRef(0);
  const pendingStartRef = useRef(null);
  // `cancel()` on an idle Chrome engine can clip the start of the next
  // utterance. Track speech this hook actually submitted to the engine so
  // a fresh read does not reset Chrome unnecessarily.
  const hasQueuedSpeechRef = useRef(false);

  // The language can change under a read in flight; the utterances already
  // queued keep the one they were built with, and the next read picks up the
  // new one without rebuilding `speak`.
  const languageRef = useRef(language);
  languageRef.current = language;

  /** Silence the engine and orphan every pending callback. Writes no state. */
  const silence = useCallback(() => {
    runRef.current += 1;
    if (pendingStartRef.current) {
      clearTimeout(pendingStartRef.current);
      pendingStartRef.current = null;
    }
    if (hasQueuedSpeechRef.current) {
      try {
        speechEngine()?.cancel?.();
      } catch {
        /* the engine no longer has this hook's speech to cancel */
      }
    }
    hasQueuedSpeechRef.current = false;
  }, []);

  const stop = useCallback(() => {
    silence();
    setSpeaking(false);
    setPaused(false);
  }, [silence]);

  const speak = useCallback(
    (text, options = {}) => {
      if (!supported) return;
      const engine = speechEngine();
      if (!engine) return;

      const chunks = chunkForSpeech(text);
      if (chunks.length === 0) return;
      const startImmediately = options?.immediate === true;

      // Anything still queued belongs to a question the room has moved on from.
      silence();
      const runId = runRef.current;

      const finish = () => {
        if (runRef.current !== runId) return;
        hasQueuedSpeechRef.current = false;
        setSpeaking(false);
        setPaused(false);
        try {
          options?.onEnd?.();
        } catch {
          /* presentation callbacks must never break speech cleanup */
        }
      };

      const abandon = () => {
        if (runRef.current !== runId) return;
        // One chunk failing leaves the rest of the answer queued behind it.
        // Drop them: a voice that resumes from the middle of an answer after a
        // gap is more confusing to a class than one that simply stopped.
        silence();
        setSpeaking(false);
        setPaused(false);
        try {
          options?.onError?.();
        } catch {
          /* presentation callbacks must never break speech cleanup */
        }
      };

      setSpeaking(true);
      setPaused(false);

      const requestedAt = Date.now();

      const enqueue = () => {
        if (runRef.current !== runId) return;

        const tag = speechLanguage(languageRef.current);
        const voices = engine.getVoices?.() || [];
        const elapsed = Date.now() - requestedAt;

        // On a cold reload Edge/Chrome often reports zero voices for a short
        // time. Waiting here avoids the first read using the mechanical default
        // and later reads suddenly switching to a Natural/Neural voice.
        if (
          !startImmediately &&
          voices.length === 0 &&
          elapsed < SPEECH_VOICE_WAIT_MAX_MS
        ) {
          pendingStartRef.current = setTimeout(
            enqueue,
            SPEECH_VOICE_POLL_MS
          );
          return;
        }

        const remainingDelay = startImmediately
          ? 0
          : Math.max(
              0,
              SPEECH_START_DELAY_MS - elapsed
            );

        if (remainingDelay > 0) {
          pendingStartRef.current = setTimeout(enqueue, remainingDelay);
          return;
        }

        pendingStartRef.current = null;
        const voice = pickVoice(voices, tag);

        hasQueuedSpeechRef.current = true;
        chunks.forEach((chunk, position) => {
          const utterance = new window.SpeechSynthesisUtterance(chunk);
          utterance.lang = tag;
          utterance.rate = SPEECH_RATE;
          utterance.pitch = SPEECH_PITCH;
          if (voice) utterance.voice = voice;
          utterance.onerror = abandon;
          if (position === chunks.length - 1) utterance.onend = finish;
          engine.speak(utterance);
        });
      };

      if (startImmediately) {
        // Keep the first audible request inside the real click/tap call stack.
        // This is the path used by "Start Professor Swan" after a reload or
        // direct entry into a saved phase on Chrome/Edge.
        engine.resume?.();
        enqueue();
      } else {
        pendingStartRef.current = setTimeout(enqueue, SPEECH_START_DELAY_MS);
      }
    },
    [silence, supported]
  );

  /** One control, both states — press to read, press again to stop. */
  const toggle = useCallback(
    (text) => {
      if (speaking) stop();
      else speak(text);
    },
    [speak, speaking, stop]
  );

  const pause = useCallback(() => {
    const engine = speechEngine();
    if (!engine || !speaking) return;
    try {
      engine.pause();
    } catch {
      /* an engine that will not pause is still speaking; leave the state alone */
      return;
    }
    setPaused(true);
  }, [speaking]);

  const resume = useCallback(() => {
    const engine = speechEngine();
    if (!engine) return;
    try {
      engine.resume();
    } catch {
      return;
    }
    setPaused(false);
  }, []);

  const togglePause = useCallback(() => {
    if (paused) resume();
    else pause();
  }, [pause, paused, resume]);

  // THE failure this hook exists to prevent. The speech engine belongs to the
  // page, not to this component, so a read survives the component that started
  // it: close the panel or move to the next slide and the voice keeps going,
  // with the only control that could stop it gone from the screen. Cancelling
  // here is not tidy-up, it is the feature. `silence` writes no state, so it is
  // safe to call after the component has gone.
  useEffect(() => () => silence(), [silence]);

  // The one hole an unmount cleanup cannot cover: a reload or a navigation is
  // not guaranteed to run React cleanups, and the engine's queue outlives the
  // document that filled it.
  useEffect(() => {
    if (!supported) return undefined;
    if (typeof window === "undefined") return undefined;
    if (typeof window.addEventListener !== "function") return undefined;

    const quiet = () => silence();
    window.addEventListener("pagehide", quiet);
    return () => window.removeEventListener("pagehide", quiet);
  }, [silence, supported]);

  return {
    supported,
    speaking,
    paused,
    speak,
    stop,
    toggle,
    pause,
    resume,
    togglePause,
  };
}
